import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedPath } from "@/lib/routes";
import onepractixLogo from "@/assets/onepractix-logo-new.svg";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [indicator, setIndicator] = useState<{ x: number; w: number; visible: boolean }>({ x: 0, w: 0, visible: false });

  // Track scroll position + reading progress (written straight to a CSS var)
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      setIsScrolled((prev) => {
        const next = y > 24;
        return prev === next ? prev : next;
      });
      const max = document.documentElement.scrollHeight - window.innerHeight;
      headerRef.current?.style.setProperty("--progress", max > 0 ? String(Math.min(1, y / max)) : "0");
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock page scroll while the mobile menu is open
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: t("nav.home"), path: getLocalizedPath("home", language) },
    { name: t("nav.services"), path: getLocalizedPath("services", language) },
    { name: t("nav.about"), path: getLocalizedPath("about", language) },
    { name: t("nav.caseStudies"), path: getLocalizedPath("caseStudies", language) },
    { name: t("nav.insights"), path: getLocalizedPath("blog", language) },
    { name: t("nav.contact"), path: getLocalizedPath("contact", language) },
  ];

  const consultationPath = getLocalizedPath("consultation", language);
  const homePath = getLocalizedPath("home", language);

  const blogPath = getLocalizedPath("blog", language);
  const isActive = (path: string) =>
    path === blogPath
      ? location.pathname === blogPath || location.pathname.startsWith(`${blogPath}/`)
      : location.pathname === path;

  const activeIndex = navLinks.findIndex((l) => isActive(l.path));
  const targetIndex = hoverIndex ?? (activeIndex >= 0 ? activeIndex : null);

  // Sliding pill indicator that follows hover, resting on the active page
  const measure = useCallback(() => {
    if (targetIndex === null) {
      setIndicator((prev) => ({ ...prev, visible: false }));
      return;
    }
    const link = linkRefs.current[targetIndex];
    const nav = navRef.current;
    if (!link || !nav) return;
    setIndicator({ x: link.offsetLeft, w: link.offsetWidth, visible: true });
  }, [targetIndex]);

  useLayoutEffect(() => {
    measure();
  }, [measure, language]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    // Re-measure once web fonts have settled (link widths change)
    document.fonts?.ready.then(measure).catch(() => {});
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return (
    <>
      <header
        ref={headerRef}
        data-scrolled={isScrolled || mobileMenuOpen}
        className="op-header sticky top-0 z-50 w-full border-b border-transparent"
      >
        <div className="container flex items-center justify-between h-16 md:h-20">
          {/* Logo + Desktop Nav grouped for balanced spacing */}
          <div className="flex items-center gap-4 xl:gap-6">
            {/* Logo (lockup unchanged) */}
            <Link
              to={homePath}
              aria-label="OnePractix — Home"
              className="flex items-center gap-0 group -ml-8 sm:-ml-[53px] md:-ml-[62px]"
              onClick={(e) => {
                if (location.pathname === homePath) {
                  e.preventDefault();
                  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                }
              }}
            >
              <img
                src={onepractixLogo}
                alt=""
                role="presentation"
                width={112}
                height={112}
                className="h-16 sm:h-24 md:h-28 w-auto object-contain -mr-2 sm:-mr-3 md:-mr-4 transition-[filter] duration-300 hover:drop-shadow-[0_4px_12px_rgba(76,175,80,0.3)]"
              />
              <span className="font-brand font-bold text-lg sm:text-2xl md:text-3xl tracking-tight">
                <span className="text-primary group-hover:text-primary/90 transition-all duration-300 group-hover:drop-shadow-[0_2px_8px_rgba(76,175,80,0.4)]">One</span>
                <span className="text-foreground group-hover:text-primary/80 transition-all duration-300 group-hover:drop-shadow-[0_2px_8px_rgba(76,175,80,0.25)]">Practix</span>
              </span>
            </Link>

            {/* Desktop Navigation — pill group with a sliding indicator */}
            <nav
              ref={navRef}
              aria-label="Main navigation"
              onMouseLeave={() => setHoverIndex(null)}
              className="op-nav hidden lg:flex items-center gap-0.5"
            >
              <span
                aria-hidden="true"
                className="op-nav-indicator"
                style={{
                  width: Math.max(0, indicator.w - 24),
                  transform: `translateX(${indicator.x + 12}px)`,
                  opacity: indicator.visible ? 1 : 0,
                }}
              />
              {navLinks.map((link, i) => (
                <Link
                  key={link.path}
                  ref={(el) => {
                    linkRefs.current[i] = el;
                  }}
                  to={link.path}
                  onMouseEnter={() => setHoverIndex(i)}
                  onFocus={() => setHoverIndex(i)}
                  onBlur={() => setHoverIndex(null)}
                  aria-current={isActive(link.path) ? "page" : undefined}
                  className={`relative z-[1] whitespace-nowrap px-3 py-2.5 text-[0.8rem] xl:text-[0.85rem] tracking-[-0.01em] transition-colors duration-300 ${
                    isActive(link.path) ? "text-foreground" : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Desktop CTA */}
          <div className="hidden items-center gap-3 lg:flex">
            <LanguageSwitcher />
            <Button variant="hero" size="sm" asChild data-magnetic="6" className="hidden xl:inline-flex px-4 text-[0.8rem] md:h-8">
              <Link to={consultationPath}>{t("nav.bookCall")}</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher />
            <button
              className="relative flex h-12 w-12 items-center justify-center rounded-full text-foreground transition-colors hover:bg-black/[0.05]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              <Menu
                size={20}
                className={`absolute transition-all duration-300 ${mobileMenuOpen ? "rotate-90 scale-50 opacity-0" : "opacity-100"}`}
              />
              <X
                size={20}
                className={`absolute transition-all duration-300 ${mobileMenuOpen ? "opacity-100" : "-rotate-90 scale-50 opacity-0"}`}
              />
            </button>
          </div>
        </div>

        <span aria-hidden="true" className="op-header-progress" />
      </header>

        {/* Mobile Menu — full-height sheet with staggered serif links.
            Rendered outside <header>: its backdrop-filter would otherwise
            become the containing block for this fixed panel. */}
        <div
          className={`lg:hidden fixed inset-x-0 top-16 md:top-20 bottom-0 z-40 bg-background transition-[opacity,visibility] duration-300 ${
            mobileMenuOpen ? "visible opacity-100" : "invisible opacity-0"
          }`}
        >
          <nav aria-label="Mobile navigation" className="container flex h-full flex-col overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6">
            <div className="flex flex-col">
              {navLinks.map((link, i) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  aria-current={isActive(link.path) ? "page" : undefined}
                  style={{ transitionDelay: mobileMenuOpen ? `${60 + i * 45}ms` : "0ms" }}
                  className={`group flex items-baseline justify-between border-b border-black/[0.08] py-4 text-[1.75rem] font-semibold tracking-[-0.025em] leading-tight transition-all duration-500 ${
                    mobileMenuOpen ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
                  } ${isActive(link.path) ? "text-primary" : "text-foreground"}`}
                >
                  <span>{link.name}</span>
                  <span className="font-sans text-xs tabular-nums text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                </Link>
              ))}
            </div>
            <div className="mt-auto pt-8">
              <Button variant="hero" size="xl" asChild className="w-full">
                <Link to={consultationPath} onClick={() => setMobileMenuOpen(false)}>
                  {t("nav.bookCall")}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </nav>
        </div>
    </>
  );
}
